import axios from 'axios';
import config from '../../../utils/config';
import { getCustomerId } from '../../../utils/cookies';
import { attachSessionExpiryInterceptor } from '../../../api/apiAuthInterceptors';
import type {
  RequestTrackingApiConfig,
  RequestTrackingItem,
  RequestTrackingListFilters,
  RequestTrackingListResponse,
  RequestTrackingStatus,
} from './types';
import { notifyRequestTrackingInvalidation } from './requestTrackingInvalidation';

const requestTrackingAxios = axios.create({
  baseURL: config.TransactionServerUrl,
  headers: {
    'cache-control': 'no-cache',
  },
});

attachSessionExpiryInterceptor(requestTrackingAxios);

const GET_ALL_REQUESTS_BY_CUSTOMER_ID_ENDPOINT = 'request/getAllRequestDetailsByCustomerID';
const CANCEL_REQUEST_ENDPOINT = 'request/cancelRequest';
const REQUESTS_DEDUPE_TTL_MS = 1500;

const inFlightRequestsByCustomerID = new Map<string, Promise<RequestTrackingItem[]>>();
const recentRequestsByCustomerID = new Map<string, { ts: number; data: RequestTrackingItem[] }>();
const inFlightListRequests = new Map<string, Promise<RequestTrackingListResponse>>();
const recentListRequests = new Map<string, { ts: number; data: RequestTrackingListResponse }>();

function toUserFriendlyError(err: unknown, fallback: string): Error {
  if (axios.isAxiosError(err)) {
    // No HTTP response -> network/CORS/timeout issue.
    if (!err.response) {
      return new Error(
        'Unable to reach the server. Please check your connection and try again.'
      );
    }

    const status = err.response.status;
    const payload = err.response.data as any;
    const serverMessage =
      (typeof payload === 'string' && payload.trim()) ||
      (typeof payload?.message === 'string' && payload.message.trim()) ||
      (typeof payload?.error === 'string' && payload.error.trim()) ||
      null;

    if (status === 400) {
      return new Error(serverMessage || 'The request could not be processed. Please verify filters and try again.');
    }
    if (status === 401 || status === 403) {
      return new Error('You are not authorized to access request tracking data. Please sign in again.');
    }
    if (status === 404) {
      return new Error('Request tracking service was not found. Please contact support if this persists.');
    }
    if (status >= 500) {
      return new Error('Request tracking service is temporarily unavailable. Please try again shortly.');
    }
    return new Error(serverMessage || fallback);
  }

  if (err instanceof Error && err.message.trim()) {
    return new Error(err.message);
  }

  return new Error(fallback);
}

function normalizeStatus(value: unknown): RequestTrackingStatus {
  const raw = String(value ?? '')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (raw === 'queued' || raw === 'pending' || raw === 'submitted') {
    return 'queued';
  }
  if (
    raw === 'running' ||
    raw === 'in_progress' ||
    raw === 'processing' ||
    raw === 'active' ||
    raw === 'started'
  ) {
    return 'running';
  }
  if (raw === 'completed' || raw === 'complete' || raw === 'success' || raw === 'succeeded') {
    return 'completed';
  }
  if (raw === 'failed' || raw === 'error' || raw === 'failure') {
    return 'failed';
  }
  if (raw === 'cancelled' || raw === 'canceled') {
    return 'cancelled';
  }
  return 'unknown';
}

const PROGRESS_FIELD_KEYS = [
  'progress',
  'progressPercentage',
  'progress_percent',
  'progressPercent',
  'requestProgress',
  'request_progress',
  'completionPercentage',
  'completion_percentage',
  'percentComplete',
  'percent_complete',
  'percentage',
  'percent',
  'completePercent',
] as const;

function parseProgressPercent(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const asPercent = parsed >= 0 && parsed <= 1 ? parsed * 100 : parsed;
  return Math.max(0, Math.min(100, asPercent));
}

function collectProgressCandidates(record: Record<string, unknown>): unknown[] {
  const candidates: unknown[] = [];
  for (const key of PROGRESS_FIELD_KEYS) {
    if (record[key] != null) {
      candidates.push(record[key]);
    }
  }
  return candidates;
}

function readStringField(
  raw: Record<string, unknown>,
  keys: string[],
): string | null {
  const dataRecord = asProgressRecord(raw.data);
  for (const key of keys) {
    const top = raw[key];
    if (top != null && String(top).trim() !== '') {
      return String(top).trim();
    }
    if (dataRecord?.[key] != null && String(dataRecord[key]).trim() !== '') {
      return String(dataRecord[key]).trim();
    }
  }
  return null;
}

function asProgressRecord(value: unknown): Record<string, unknown> | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

/** Progress % lives on the item and/or inside its `data` payload from the tracker API. */
function extractRequestProgress(raw: Record<string, unknown>): number | null {
  const candidates: unknown[] = collectProgressCandidates(raw);

  const dataRecord = asProgressRecord(raw.data);
  if (dataRecord) {
    candidates.push(...collectProgressCandidates(dataRecord));

    // PROJECT_BASELINE_CREATE (and similar) often report processedTasks / totalTasks.
    const processed = Number(
      dataRecord.processedTasks ?? dataRecord.processed_tasks ?? dataRecord.taskCount,
    );
    const total = Number(dataRecord.totalTasks ?? dataRecord.total_tasks);
    if (Number.isFinite(processed) && Number.isFinite(total) && total > 0) {
      candidates.push((processed / total) * 100);
    }
  }

  for (const candidate of candidates) {
    const percent = parseProgressPercent(candidate);
    if (percent != null) {
      return percent;
    }
  }
  return null;
}

function requestTypeLooksLikeBaseline(requestType: string | null | undefined): boolean {
  const t = String(requestType ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  if (!t || t.includes('WBS')) return false;
  return t.includes('BASELINE');
}

/**
 * Human-readable label for tracker cards.
 * Baseline jobs use nested `baselineName` / `baselineIndex` instead of a file name.
 */
function resolveRequestDisplayName(
  raw: Record<string, unknown>,
  requestType: string | null,
  dataRecord: Record<string, unknown> | null,
): string {
  if (requestTypeLooksLikeBaseline(requestType)) {
    const baselineName = readStringField(raw, ['baselineName', 'baseline_name']);
    if (baselineName) return baselineName;

    const indexRaw =
      raw.baselineIndex ??
      raw.baseline_index ??
      dataRecord?.baselineIndex ??
      dataRecord?.baseline_index;
    const index = Number(indexRaw);
    if (Number.isFinite(index)) {
      return `Baseline ${index}`;
    }
    return 'Project baseline';
  }

  const explicitFile =
    readStringField(raw, ['fileName', 'file_name', 'filename']) ||
    String(raw.title ?? '').trim();
  if (explicitFile) return explicitFile;
  if (requestType) {
    return String(requestType)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return 'Unknown request';
}

function resolveRequestMessage(
  raw: Record<string, unknown>,
  requestType: string | null,
  dataRecord: Record<string, unknown> | null,
): string | null {
  const explicit =
    (typeof raw.message === 'string' && raw.message.trim()) ||
    (typeof raw.description === 'string' && raw.description.trim()) ||
    null;
  if (explicit) return explicit;

  if (!requestTypeLooksLikeBaseline(requestType) || !dataRecord) {
    return null;
  }

  const processed = Number(
    dataRecord.processedTasks ?? dataRecord.processed_tasks ?? dataRecord.taskCount,
  );
  const total = Number(dataRecord.totalTasks ?? dataRecord.total_tasks);
  if (Number.isFinite(processed) && Number.isFinite(total) && total > 0) {
    return `${processed.toLocaleString()} / ${total.toLocaleString()} tasks`;
  }
  const baselineName = dataRecord.baselineName ?? dataRecord.baseline_name;
  const index = dataRecord.baselineIndex ?? dataRecord.baseline_index;
  if (baselineName != null && String(baselineName).trim()) {
    return String(baselineName);
  }
  if (index != null && Number.isFinite(Number(index))) {
    return `Baseline index ${index}`;
  }
  return null;
}

function extractRequestListPayload(data: unknown): unknown[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (data != null && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) {
      return obj.data;
    }
    if (Array.isArray(obj.requests)) {
      return obj.requests;
    }
    // Single-request status payload: { status, data: { requestID, requestType, ... } }
    const nested = obj.data;
    if (
      nested != null &&
      typeof nested === 'object' &&
      !Array.isArray(nested) &&
      ((nested as Record<string, unknown>).requestID != null ||
        (nested as Record<string, unknown>).requestId != null ||
        (nested as Record<string, unknown>).requestType != null)
    ) {
      return [nested];
    }
  }
  return [];
}

function resolveCustomerId(_filters: RequestTrackingListFilters): string {
  void _filters;
  return getCustomerId();
}

/** Clear cached list responses (e.g. after starting a new import). */
export function invalidateRequestTrackingCache(customerId?: string): void {
  if (customerId) {
    const key = String(customerId);
    recentRequestsByCustomerID.delete(key);
    inFlightRequestsByCustomerID.delete(key);
  } else {
    recentRequestsByCustomerID.clear();
    inFlightRequestsByCustomerID.clear();
  }
  recentListRequests.clear();
  inFlightListRequests.clear();
  notifyRequestTrackingInvalidation();
}

function normalizeItem(raw: any): RequestTrackingItem {
  const requestTypeRaw = raw?.requestType ?? raw?.request_type ?? raw?.type ?? null;
  const requestType = requestTypeRaw ? String(requestTypeRaw) : null;
  const rawRecord =
    raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const dataPayload = rawRecord.data;
  const dataRecord = asProgressRecord(dataPayload);
  const normalizedProgress = extractRequestProgress(rawRecord);
  const requestId = String(
    raw?.requestId ??
      raw?.requestID ??
      raw?.request_id ??
      raw?.refId ??
      raw?.ref_id ??
      raw?.id ??
      ''
  );
  const fileName = resolveRequestDisplayName(rawRecord, requestType, dataRecord);
  const userName =
    readStringField(rawRecord, ['userName', 'user_name', 'user', 'submittedBy', 'submitted_by']) ??
    null;
  const requestTime =
    readStringField(rawRecord, ['requestTime', 'request_time', 'createdAt', 'created_at']) ?? null;
  const requestCompletedTime =
    readStringField(rawRecord, [
      'requestCompletedTime',
      'request_completed_time',
      'completedAt',
      'completed_at',
    ]) ?? null;

  return {
    requestId,
    fileName,
    title: fileName,
    status: normalizeStatus(raw?.status ?? raw?.requestStatus ?? raw?.request_status),
    progress: normalizedProgress,
    userName,
    requestTime,
    requestCompletedTime,
    submittedBy: userName,
    createdAt: requestTime,
    startedAt: raw?.startedAt ?? raw?.started_at ?? null,
    completedAt: requestCompletedTime,
    message: resolveRequestMessage(rawRecord, requestType, dataRecord),
    requestType,
    source: raw?.source ?? raw?.module ?? requestType,
    metadata:
      dataRecord ??
      (dataPayload != null && typeof dataPayload === 'object' ? dataPayload : null) ??
      raw?.metadata ??
      raw?.meta ??
      null,
  };
}

export function setRequestTrackingAuthToken(token: string | null) {
  if (token) {
    requestTrackingAxios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete requestTrackingAxios.defaults.headers.common.Authorization;
  }
}

export async function fetchTrackedRequests(
  filters: RequestTrackingListFilters,
  apiConfig?: RequestTrackingApiConfig
): Promise<RequestTrackingListResponse> {
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? 20;
  const customerId = resolveCustomerId(filters);
  const requestKey = JSON.stringify({
    customerId,
    status: filters.status ?? 'all',
    page,
    pageSize,
  });

  const now = Date.now();
  const cachedList = recentListRequests.get(requestKey);
  if (cachedList && now - cachedList.ts < REQUESTS_DEDUPE_TTL_MS) {
    return cachedList.data;
  }
  const existingInFlightList = inFlightListRequests.get(requestKey);
  if (existingInFlightList) {
    return existingInFlightList;
  }

  const requestPromise = (async () => {
  try {
    void apiConfig;

    if (!customerId) {
      return {
        data: [],
        total: 0,
        page,
        pageSize,
      };
    }

    const allItems = await getAllRequestsByCustomerID(customerId);
    const statusFiltered =
      filters.status && filters.status !== 'all'
        ? allItems.filter((item) => item.status === filters.status)
        : allItems;

    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const pagedItems = statusFiltered.slice(start, end);

    const result = {
      data: pagedItems,
      total: statusFiltered.length,
      page,
      pageSize,
    };
    recentListRequests.set(requestKey, { ts: Date.now(), data: result });
    return result;
  } catch (err) {
    console.error('Error fetching request tracking data:', err);
    throw toUserFriendlyError(err, 'Unable to load request updates right now.');
  } finally {
    inFlightListRequests.delete(requestKey);
  }
  })();

  inFlightListRequests.set(requestKey, requestPromise);
  return requestPromise;
}

export async function getAllRequestsByCustomerID(
  customerID: string
): Promise<RequestTrackingItem[]> {
  const cacheKey = String(customerID);
  const now = Date.now();
  const cached = recentRequestsByCustomerID.get(cacheKey);
  if (cached && now - cached.ts < REQUESTS_DEDUPE_TTL_MS) {
    return cached.data;
  }

  const existingPromise = inFlightRequestsByCustomerID.get(cacheKey);
  if (existingPromise) {
    return existingPromise;
  }

  const requestPromise = (async () => {
    try {
      const res = await requestTrackingAxios.get(GET_ALL_REQUESTS_BY_CUSTOMER_ID_ENDPOINT, {
        params: { customerID },
      });
      const itemsRaw = extractRequestListPayload(res.data);
      const normalizedItems: RequestTrackingItem[] = itemsRaw.map(normalizeItem);
      const filteredItems = normalizedItems.filter((item) => item.requestId !== '');
      recentRequestsByCustomerID.set(cacheKey, { ts: Date.now(), data: filteredItems });
      return filteredItems;
    } catch (err) {
      console.error('Error fetching requests by customer ID:', err);
      throw toUserFriendlyError(err, 'Unable to load request updates right now.');
    } finally {
      inFlightRequestsByCustomerID.delete(cacheKey);
    }
  })();

  inFlightRequestsByCustomerID.set(cacheKey, requestPromise);

  try {
    return await requestPromise;
  } catch (err) {
    throw err;
  }
}

export async function cancelTrackedRequest(
  requestId: string,
  _apiConfig?: RequestTrackingApiConfig
): Promise<void> {
  void _apiConfig;
  try {
    const formData = new FormData();
    formData.append('requestID', requestId);
    await requestTrackingAxios.put(CANCEL_REQUEST_ENDPOINT, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    invalidateRequestTrackingCache();
  } catch (err) {
    console.error('Error cancelling tracked request:', err);
    throw toUserFriendlyError(err, 'Unable to cancel this request right now.');
  }
}
